import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["customText", "passageList", "confirmButton", "noPassages"];
  static values = {
    fieldName: String,
    stationerySelectionId: String,
    updateUrl: String
  };

  connect() {
    this.selectedPassageText = null;
    this.selectedPassageId = null;
    this.setupFilterCheckboxes();
    this.filterPassages();
  }

  passageListTargetConnected() {
    this.filterPassages();
  }

  filterPassages() {
    if (!this.hasPassageListTarget) return;

    const checkedFilters = this.getCheckedFilters();
    let visibleCount = 0;
    this.passageListTarget.querySelectorAll('[data-passage-type]').forEach(item => {
      const visible = checkedFilters.includes(item.dataset.passageType);
      item.style.display = visible ? "" : "none";
      if (visible) visibleCount++;
    });

    if (this.hasNoPassagesTarget) {
      this.noPassagesTarget.classList.toggle("hidden", visibleCount > 0);
    }
  }

  selectPassage(event) {
    this.selectedPassageText = event.target.dataset.passageText;
    this.selectedPassageId = event.target.value;

    if (this.hasCustomTextTarget) {
      this.customTextTarget.value = "";
    }
  }

  previousModal(event) {
    event.preventDefault();

    const { displayModalId, selectionModalId } = event.currentTarget.dataset;

    if (displayModalId && selectionModalId && window.HSOverlay) {
      window.HSOverlay.close(`#${displayModalId}`);
      window.HSOverlay.open(`#${selectionModalId}`);
    }
  }

  async selectAndConfirm(event) {
    event.preventDefault();

    const passageText = event.currentTarget.dataset.passageText;
    if (!passageText) return;

    this.selectedPassageText = passageText;

    if (this.hasCustomTextTarget) this.customTextTarget.value = "";

    await this.saveSelection();

    this.closeModal(event.currentTarget.dataset.displayModalId);
  }

  async confirmSelection(event) {
    event.preventDefault();
    await this.saveSelection();
  }

  // Private

  setupFilterCheckboxes() {
    this.element.querySelectorAll('input[name="passage_type_filter"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.filterPassages());
    });
  }

  getCheckedFilters() {
    return Array.from(this.element.querySelectorAll('input[name="passage_type_filter"]:checked'))
      .map(checkbox => checkbox.value)
      .filter(Boolean);
  }

  getValueToSave() {
    const customText = this.hasCustomTextTarget && this.customTextTarget.value.trim();
    return customText || this.selectedPassageText || null;
  }

  async saveSelection() {
    const valueToSave = this.getValueToSave();

    if (!valueToSave) {
      alert("Please either write a custom passage or select a passage from the list");
      return;
    }

    this.confirmButtonTarget.disabled = true;

    try {
      await this.sendSaveRequest(valueToSave);
      this.closeCurrentModal();
      window.location.reload();
    } catch (error) {
      this.confirmButtonTarget.disabled = false;
      console.error("Error saving passage:", error);
      alert(`An error occurred while saving: ${error.message}`);
    }
  }

  async sendSaveRequest(valueToSave) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

    if (!csrfToken) throw new Error("CSRF token not found");

    const response = await fetch(this.updateUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        stationery_selection_id: this.stationerySelectionIdValue,
        data_content: {
          field_name: this.fieldNameValue,
          field_value: valueToSave
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.errors?.join(", ") || "Failed to save");
    }

    const data = await response.json();

    if (!data.success) throw new Error(data.errors?.join(", ") || "Failed to save");

    return data;
  }

  closeCurrentModal() {
    const modalElement = this.element.closest('[id^="modal-"]');
    this.closeModal(modalElement?.id);
  }

  closeModal(modalId) {
    if (modalId && window.HSOverlay) window.HSOverlay.close(`#${modalId}`);
  }
}
